<template>
  <div class="flex flex-col flex-1 min-h-0 w-full">
    <!-- Tab bar -->
    <div class="flex border-b border-gray-300 shrink-0">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="flex-1 basis-0 py-2.5 text-sm whitespace-nowrap transition-colors text-center"
        :class="[
          activeTab === tab.key
            ? 'font-semibold text-gray-600 border-b-2 border-gray-600 border-solid -mb-px'
            : 'font-normal text-gray-500 hover:text-gray-600',
        ]"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Custom Fields tab -->
    <div
      v-if="activeTab === 'custom-fields'"
      class="flex-1 min-h-0 overflow-y-auto custom-scroll"
    >
      <ConversationCustomAttributes :contact="contact" />
    </div>

    <!-- Conversations tab -->
    <div
      v-else-if="activeTab === 'conversations'"
      ref="conversationList"
      class="conversation-list flex-1 min-h-0 overflow-y-auto custom-scroll py-3"
      :class="{
        'ltr:pl-4 ltr:pr-[8px] rtl:pr-4 rtl:pl-[8px]': hasScroll,
        'px-4': !hasScroll,
      }"
    >
      <div v-if="isFetchingConversations" class="flex justify-center mt-6">
        <spinner />
      </div>
      <template v-else-if="previousConversations.length">
        <div class="flex flex-col gap-2">
          <conversation-card
            v-for="chat in previousConversations"
            :key="chat.id"
            :chat="chat"
            :hide-context-menu="true"
          />
        </div>
      </template>
      <p v-else class="text-sm text-gray-400 text-center mt-6">
        {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
      </p>
    </div>

    <!-- Files tab -->
    <div
      v-else-if="activeTab === 'files'"
      class="flex-1 min-h-0 overflow-y-auto custom-scroll"
    >
      <conversation-files :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import ConversationCard from 'dashboard/routes/dashboard/conversation/ConversationCard/Index.vue';
import Spinner from 'shared/components/Spinner.vue';
import ConversationFiles from './ConversationFiles.vue';
import ConversationCustomAttributes from 'dashboard/routes/dashboard/conversation/CustomAttributesV2/Index.vue';

export default {
  name: 'ContactTabs',
  components: {
    ConversationCustomAttributes,
    ConversationCard,
    Spinner,
    ConversationFiles,
  },
  props: {
    contact: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 'custom-fields',
      hasScroll: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      uiFlags: 'contactConversations/getUIFlags',
    }),
    tabs() {
      return [
        {
          key: 'custom-fields',
          label: this.$t('CONTACT_PANEL.TABS.CUSTOM_FIELDS'),
        },
        {
          key: 'conversations',
          label: this.$t('CONTACT_PANEL.TABS.CONVERSATIONS'),
        },
        {
          key: 'files',
          label: this.$t('CONTACT_PANEL.TABS.FILES'),
        },
      ];
    },
    isFetchingConversations() {
      return this.uiFlags.isFetching;
    },
    conversations() {
      return this.$store.getters['contactConversations/getContactConversation'](
        this.contact.id
      );
    },
    previousConversations() {
      return this.conversations.filter(
        c => c.id !== Number(this.currentChat.id)
      );
    },
  },
  watch: {
    'contact.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        this.$store.dispatch('contactConversations/get', newId);
      }
    },
    previousConversations() {
      this.$nextTick(() => {
        const el = this.$refs.conversationList;
        if (el) this.hasScroll = el.scrollHeight > el.clientHeight;
      });
    },
  },
  mounted() {
    if (this.contact.id) {
      this.$store.dispatch('contactConversations/get', this.contact.id);
    }
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .conversation {
    @apply bg-white rounded-lg overflow-hidden border-x-0 border-b border-neutral-200;
  }
}
</style>
